import { FormGroup, Input, Label, Row, Col } from "reactstrap";
import DefaultModal from "./Default";
import './SearchFilters.css';
import DefaultButton from "../Buttons/Default";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { SelectCustomStyles } from "../../utils/styles";
import { useTranslation } from "react-i18next";
import { tField } from "../../utils/helpers";

function SearchFiltersModal({isOpen, toggle, categories, states}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [formValues, setFormValues] = useState({
    property_for: 'rent',
    category_id: '',
    city_ids: [],
    keyword: '',
    min_price: '',
    max_price: ''
  });

  useEffect(() => {
    if (isOpen) {
      const params = new URLSearchParams(location.search);
      const propertyFor = params.get("property_for") || "rent";
      const min = params.get("min_price") || "";
      const max = params.get("max_price") || "";
      const categoryId = params.get("category_id") ? Number(params.get("category_id")) : "";
      const cityIds = params.getAll("city_ids[]").map(id => Number(id));
      const keyword = params.get("keyword") || "";
      setFormValues({ 
        property_for: propertyFor, 
        min_price: min, 
        max_price: max, 
        category_id: categoryId, 
        city_ids: cityIds,
        keyword: keyword
      });
    }
  }, [isOpen, location.search]);

  const convertToEnglishDigits = (value) => {
    return value
      .replace(/[٠-٩]/g, d => d.charCodeAt(0) - 0x0660)
      .replace(/[۰-۹]/g, d => d.charCodeAt(0) - 0x06F0)
      .replace(/[^0-9]/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: convertToEnglishDigits(value) });
  }

  const handleSearch = () => {
    const params = new URLSearchParams(location.search);

    if (formValues.min_price) params.set("min_price", formValues.min_price);
    else params.delete("min_price");

    if (formValues.max_price) params.set("max_price", formValues.max_price);
    else params.delete("max_price");

    if (formValues.property_for) params.set("property_for", formValues.property_for);
    else params.delete("property_for");

    if (formValues.category_id) params.set("category_id", formValues.category_id);
    else params.delete("category_id");

    if (formValues.city_ids.length > 0) {
      params.delete("city_ids[]");
      formValues.city_ids.forEach(cityId => {
        params.append("city_ids[]", cityId);
      });
    } else {
      params.delete("city_ids[]");
    }

    if (formValues.keyword) params.set("keyword", formValues.keyword);
    else params.delete("keyword");

    const q = params.toString();
    navigate(`${location.pathname}${q ? `?${q}` : ""}`);
    toggle && toggle(false);
  };
  
  return(
    <DefaultModal
      isOpen={isOpen}
      toggle={toggle}
      title={t('search.filter.title')}
      size="md"
    >
      <div className="filter-type-tabs">
        <Input
          type="radio"
          name="property_for"
          id="filter_sale"
          className="filter-type-input"
          onChange={() => setFormValues({ ...formValues, property_for: 'sale' })}
          checked={formValues.property_for === 'sale'}
        />
        <Label for="filter_sale" className="filter-type-label">
          {t('search.filter.fields.purpose.sale')}
        </Label>

        <Input
          type="radio"
          name="property_for"
          id="filter_rent"
          className="filter-type-input"
          onChange={() => setFormValues({ ...formValues, property_for: 'rent' })}
          checked={formValues.property_for === 'rent'}
        />
        <Label for="filter_rent" className="filter-type-label">
          {t('search.filter.fields.purpose.rent')}
        </Label>

        <Input
          type="radio"
          name="property_for"
          id="filter_exchange"
          className="filter-type-input"
          onChange={() => setFormValues({ ...formValues, property_for: 'exchange' })}
          checked={formValues.property_for === 'exchange'}
        />
        <Label for="filter_exchange" className="filter-type-label">
          {t('search.filter.fields.purpose.exchange')}
        </Label>
      </div>

      <FormGroup className="select-group">
        <div className="select-wrapper">
          <Select
            placeholder={t('search.filter.fields.category.placeholder')}
            options={categories.map(cat => ({ value: cat.id, label: tField(cat, 'name') }))}
            onChange={(option) => setFormValues({ ...formValues, category_id: option.value })}
            className="custom-select"
            styles={SelectCustomStyles}
            value={categories.find(cat => cat.id === formValues.category_id) ? { value: formValues.category_id, label: tField(categories.find(cat => cat.id === formValues.category_id), 'name') } : null}
          />
        </div>
      </FormGroup>

      <FormGroup className="select-group">
        <div className="select-wrapper">
          <Select
            placeholder={t('search.filter.fields.city.placeholder')}
            options={states.map(state => ({
              label: tField(state, 'name'), // state name (disabled group label)
              options: state.cities.map(city => ({
                value: city.id,
                label: tField(city, 'name'),
              })),
            }))}
            onChange={(selectedOptions) => {
              setFormValues({
                ...formValues,
                city_ids: selectedOptions.map(opt => opt.value),
              });
            }}
            styles={SelectCustomStyles}
            className="custom-select"
            isMulti
            closeMenuOnSelect={false}
            value={states.flatMap(s => s.cities).filter(city => formValues.city_ids.includes(city.id)).map(city => ({ value: city.id, label: tField(city, 'name') }))}
          />
        </div>
      </FormGroup>

      <FormGroup>
        <Input 
          type="text"
          placeholder={t('search.filter.fields.keyword.placeholder')}
          name="keyword"
          value={formValues.keyword || ''}
          onChange={(e) => setFormValues({ ...formValues, keyword: e.target.value })}
        />
      </FormGroup>

      <h6 className="fw-bold">{t('search.filter.fields.price.title')}</h6>
      <Row>
        <Col xs="6" className="mb-3">
          <Label>{t('search.filter.fields.price.from_label')}: </Label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder={t('search.filter.fields.price.from_placeholder')}
            name="min_price"
            value={formValues.min_price}
            onChange={handleInputChange}
          />
        </Col>
        <Col xs="6" className="mb-3">
          <Label>{t('search.filter.fields.price.to_label')}: </Label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder={t('search.filter.fields.price.to_placeholder')}
            name="max_price"
            value={formValues.max_price}
            onChange={handleInputChange}
          />
        </Col>
      </Row>

      <DefaultButton block onClick={handleSearch}>
        {t('search.filter.button')}
      </DefaultButton>
    </DefaultModal>
  );
}

export default SearchFiltersModal;